import React, { createContext, useState, useEffect, useRef } from 'react';
import { createAPIClient, APIError, API_ERROR_TYPES } from '../api/client';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const apiClientRef = useRef(null);
    const [userState, setUserState] = useState({
        isLoading: true,
        error: null,
        data: null,
        userData: null,
        teams: {
            admin: [],
            member: []
        },
        organizations: {
            admin: [],
            member: []
        },
        permissions: {
            hasAdminTeams: false,
            hasMemberTeams: false,
            hasOrganizations: false,
            isOrganizationAdmin: false
        }
    });

    const fetchUserData = async () => {
        try {
            setUserState(prev => ({ ...prev, isLoading: true, error: null }));

            if (!apiClientRef.current) {
                apiClientRef.current = await createAPIClient();
            }

            const data = await apiClientRef.current.profiles.getCurrentUser();

            // Transform raw organizations data into the expected structure
            const transformedOrganizations = {
                admin: [],
                member: []
            };

            // Check if raw organizations data exists
            if (data.raw?.organizations?.length) {
                data.raw.organizations.forEach(org => {
                    const transformedOrg = {
                        name: org.organization?.name,
                        organizationId: org.organization_id,
                        role: org.role,
                        status: org.status,
                        description: org.organization?.description,
                        route: org.organization?.route,
                        isAdmin: org.role === 'admin'
                    };

                    // Add to appropriate array based on role
                    if (org.role === 'admin') {
                        transformedOrganizations.admin.push(transformedOrg);
                    } else {
                        transformedOrganizations.member.push(transformedOrg);
                    }
                });
            }

            const newState = {
                isLoading: false,
                error: null,
                data: data.raw,
                userData: data.userData,
                teams: data.teams,
                organizations: transformedOrganizations,  // Use our transformed organizations
                permissions: data.permissions
            };

            setUserState(newState);
            // console.log(newState);

        } catch (error) {
            console.error('Error fetching user data:', error);

            const errorMessage = error instanceof APIError
                ? error.message
                : 'Failed to fetch user data';

            setUserState({
                isLoading: false,
                error: errorMessage,
                data: null,
                userData: null,
                teams: null,
                organizations: null,
                permissions: null
            });
        }
    };

    // Expose methods to update user state
    const refreshUserData = () => {
        return fetchUserData();
    };

    const clearUserData = () => {
        setUserState({
            isLoading: false,
            error: null,
            data: null,
            userData: null,
            teams: {
                admin: [],
                member: []
            },
            organizations: {
                admin: [],
                member: []
            },
            permissions: {
                hasAdminTeams: false,
                hasMemberTeams: false,
                hasOrganizations: false,
                isOrganizationAdmin: false
            }
        });
    };

    // Initial fetch on mount
    useEffect(() => {
        fetchUserData();
    }, []);

    const value = {
        ...userState,
        refreshUserData,
        clearUserData,
        setUserState
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
